import { useEffect } from 'react';

import { L3_PATHS } from 'Deer/constants/routes';
import { useAuthenticatedUser } from 'components/hooks/dataHooks';
import queryString from 'query-string';
import { useSelector } from 'react-redux';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { getRelationToLoggedInEmployeeApi } from 'screens/oneOnOneRabbit/InstancePage/apis';
import { selectEnabledFeatureFlags } from 'selectors/enabledFeatureFlags';
import { selectIsAuthenticated } from 'selectors/loggedInEmployee';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

type OneOnOneRedirectConfig = {
  rabbitRoute: string;
  deerRoutes: {
    default: string;
    reportee: string;
    skipReportee: string;
  };
  routeType: 'SIMPLE' | 'PARAMETERIZED';
};

const getEmployeeRelation = async (employeeId: string) => {
  let relation = null;
  try {
    relation = await getRelationToLoggedInEmployeeApi({
      employeeId,
    });
  } catch (error) {
    console.error('Error in fetching relation to logged in employee. Redirecting normally', error);
  }
  return relation;
};

// Function to replace parameters in any route dynamically
const replaceParamsInRoute = (routeTemplate: string, pathParams: Record<string, string | number>): string => {
  let route = routeTemplate;

  // Replace each parameter in the route template with the corresponding value
  Object.keys(pathParams).forEach((paramName) => {
    const paramValue = pathParams[paramName];
    route = route.replace(`:${paramName}`, String(paramValue));
  });

  return route;
};

const redirectionLogger = ({
  rabbitRoute,
  deerRoute,
  redirectionType,
}: {
  rabbitRoute: string;
  deerRoute: string;
  redirectionType: 'rabbitRoute -> deerRoute';
}) => {
  const isDev = process.env.NODE_ENV === 'development' && process.env.PUBLIC_APP_ENABLE_REDIRECTION_LOGGER === 'true';

  if (isDev) {
    console.log('\x1b[34m%s\x1b[0m', 'OneOnOne Redirection Log:');
    console.log('\x1b[35m%s\x1b[0m', 'rabbitRoute:', rabbitRoute);
    console.log('\x1b[36m%s\x1b[0m', 'deerRoute:', deerRoute);
    console.log('\x1b[32m%s\x1b[0m', 'redirectionType:', redirectionType);
    console.log('\x1b[34m%s\x1b[0m', '----------------------');
  }
};

const oneOnOneRedirectConfigs: Array<OneOnOneRedirectConfig> = [
  {
    rabbitRoute: '/app/oneOnOne',
    deerRoutes: {
      default: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
      reportee: L3_PATHS.team.directReports.oneOnOne.manage,
      skipReportee: L3_PATHS.team.allReports.oneOnOne.manage,
    },
    routeType: 'SIMPLE',
  },
  {
    rabbitRoute: '/app/oneOnOne/:masterId',
    deerRoutes: {
      default: L3_PATHS['my-data'].oneOnOne.masterPage,
      reportee: L3_PATHS.team.directReports.oneOnOne.masterPage,
      skipReportee: L3_PATHS.team.allReports.oneOnOne.masterPage,
    },
    routeType: 'PARAMETERIZED',
  },
  {
    rabbitRoute: '/app/oneOnOne/:masterId/:instanceId',
    deerRoutes: {
      default: L3_PATHS['my-data'].oneOnOne.instancePage,
      reportee: L3_PATHS.team.directReports.oneOnOne.instancePage,
      skipReportee: L3_PATHS.team.allReports.oneOnOne.instancePage,
    },
    routeType: 'PARAMETERIZED',
  },
  {
    rabbitRoute: '/app/oneOnOne/audit',
    deerRoutes: {
      default: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
      reportee: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
      skipReportee: L3_PATHS['my-data'].oneOnOne.myOneOnOne,
    },
    routeType: 'SIMPLE',
  },
];

const useOneOnOneRedirection = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const history = useHistory();
  const location = useLocation();
  const isNewNavEnabled = checkNewNavEnabled();
  const { user } = useAuthenticatedUser();

  const enabledFeatures = useSelector(selectEnabledFeatureFlags);
  const featureFlagsLoaded = enabledFeatures !== null;
  const isUserLoggedIn = !!user;

  useEffect(() => {
    // defer redirection until the user is authenticated and feature flags are loaded
    if (!isAuthenticated || !isNewNavEnabled) {
      return;
    }

    const handleOneOnOneRedirection = async () => {
      const currentPath = location.pathname;
      const queryParams = queryString.parse(location.search);
      const otherEmployeeId = queryParams.otherEmpId as string;

      // eslint-disable-next-line no-restricted-syntax
      for (const config of oneOnOneRedirectConfigs) {
        const { rabbitRoute, deerRoutes, routeType } = config;

        let isRouteMatch = false;
        let routeParams = {};

        if (routeType === 'SIMPLE') {
          isRouteMatch = currentPath === rabbitRoute;
        } else if (routeType === 'PARAMETERIZED') {
          const matchResult = matchPath(currentPath, {
            path: rabbitRoute,
            exact: true,
            strict: false,
          });

          if (matchResult) {
            isRouteMatch = true;
            routeParams = matchResult.params || {};
          }
        }

        if (isRouteMatch) {
          let targetDeerRoute = deerRoutes.default;

          // Determine the correct deer route based on employee relation
          if (otherEmployeeId) {
            const relation = await getEmployeeRelation(otherEmployeeId);

            if (relation?.entity === 'REPORTEE') {
              targetDeerRoute = deerRoutes.reportee;
            } else if (relation?.entity === 'SKIP_REPORTEE') {
              targetDeerRoute = deerRoutes.skipReportee;
            }
          }

          // Replace parameters in the target route if needed
          if (routeType === 'PARAMETERIZED' && Object.keys(routeParams).length > 0) {
            targetDeerRoute = replaceParamsInRoute(targetDeerRoute, routeParams);
          }

          // Handle special case for audit route with oneOnOneType query param
          if (rabbitRoute === '/app/oneOnOne/audit') {
            const newQueryParams = new URLSearchParams();
            const oneOnOneType = queryParams.oneOnOneType as string;

            // Append oneOnOneType query param (default to ACTIVE if not present)
            newQueryParams.append('oneOnOneType', oneOnOneType || 'ACTIVE');

            // Append other query params if they exist
            if (otherEmployeeId) {
              newQueryParams.append('otherEmpId', otherEmployeeId);
            }

            redirectionLogger({
              rabbitRoute: `${rabbitRoute}?${location.search}`,
              deerRoute: `${targetDeerRoute}?${newQueryParams.toString()}`,
              redirectionType: 'rabbitRoute -> deerRoute',
            });

            history.replace({
              pathname: targetDeerRoute,
              search: newQueryParams.toString(),
            });
            return;
          }

          // For other routes, preserve existing query params and add oneOnOneType=ACTIVE for manage pages
          const updatedQueryParams = { ...queryParams };
          const isManagePage = targetDeerRoute.includes('/manage/');
          const isMyOneOnOnePage = targetDeerRoute === L3_PATHS['my-data'].oneOnOne.myOneOnOne;

          // Add oneOnOneType=ACTIVE if it's a manage page and oneOnOneType is not already present
          if ((isManagePage || isMyOneOnOnePage) && !updatedQueryParams.oneOnOneType) {
            updatedQueryParams.oneOnOneType = 'ACTIVE';
          }

          const finalRoute =
            Object.keys(updatedQueryParams).length > 0
              ? `${targetDeerRoute}?${queryString.stringify(updatedQueryParams)}`
              : targetDeerRoute;

          redirectionLogger({
            rabbitRoute: `${rabbitRoute}${location.search ? `?${location.search}` : ''}`,
            deerRoute: finalRoute,
            redirectionType: 'rabbitRoute -> deerRoute',
          });

          history.replace(finalRoute);
          return;
        }
      }
    };

    if (isUserLoggedIn && featureFlagsLoaded) {
      handleOneOnOneRedirection();
    }
  }, [history, location, isNewNavEnabled, isUserLoggedIn, featureFlagsLoaded, isAuthenticated]);
};

export { useOneOnOneRedirection };
